'use client';

import Image from 'next/image';
import Link from 'next/link';

import { cn, useBookButtonAction } from '@/lib';
import type { Place, TypesenseEventDocument } from '@/types';
import { IconMapPinFilled, IconPhoto } from '@tabler/icons-react';
import { format } from 'date-fns';

import { usePlacePrefix } from '@/lib/context/venues-page-context';
import { useGlobal } from '@/lib/global-store';
import { formatTimeRange, imageMetaConverter } from '@/lib/server';
import { getBookButtonText } from '@/lib/utils/get-book-button-text';

import { Button } from '../button';
import { RichText } from '../rich-text';
import { Text } from '../text';
import { EventActions } from './event-actions';

interface EventCardProps extends TypesenseEventDocument {
  place: Place | undefined;
  size?: 'sm' | 'default' | 'lg';
  dateFormat?: string;
}

const sizes = {
  lg: {
    root: 'flex flex-col items-start gap-2 pb-3',
    content: 'flex w-full flex-col items-start gap-1 text-start',
    image:
      'border-border relative flex aspect-[3/2] w-full items-center justify-center overflow-hidden rounded-lg border',
    imageSize: '(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1280px) 33vw, 320px',
  },
  default: {
    root: 'border-border/25 flex items-start gap-3 border-b pb-3 sm:gap-5 sm:pb-5',
    content: 'flex w-0 grow flex-col items-start gap-1 text-start',
    image:
      'border-border relative mt-1.5 flex size-18 sm:size-36 shrink-0 items-center justify-center overflow-hidden rounded-lg border',
    imageSize: '(max-width: 640px) 72px, 144px',
  },
  sm: {
    root: 'border-border/25 flex items-start gap-3 border-b pb-4',
    content: 'flex w-0 grow flex-col items-start gap-1 text-start',
    image:
      'border-border relative mt-1.5 flex size-18 shrink-0 items-center justify-center overflow-hidden rounded-lg border',
    imageSize: '72px',
  },
};

export function EventCardSkeleton({ size = 'default' }: { size?: 'sm' | 'default' | 'lg' }) {
  return (
    <div className={cn('animate-pulse', sizes[size].root)}>
      <div className={sizes[size].image}>
        <IconPhoto className="text-muted size-12" />
      </div>

      <div className={sizes[size].content}>
        <div className="flex w-full flex-col items-start">
          <span className="text-muted bg-muted my-1 h-3.5 w-1/2 rounded-lg" />
          <span className="text-muted bg-muted my-1 h-4 w-3/4 rounded-lg sm:h-4.5" />
        </div>

        <span className="text-muted bg-muted h-3.5 w-1/3 rounded-lg" />
        <span className="text-muted bg-muted h-3.5 w-3/5 rounded-lg" />
      </div>
    </div>
  );
}

export function EventCard({ place, size = 'default', dateFormat, ...event }: EventCardProps) {
  const { setOpenBook } = useGlobal();
  const onClick = useBookButtonAction(event, place);
  const prefix = usePlacePrefix();

  const buttonText = getBookButtonText(event);
  const date = new Date(event.dates * 1000);
  const image = imageMetaConverter(event.image ?? undefined);
  const href = `${prefix}/events/${event.slug}?date=${date.toISOString()}`;

  return (
    <div className={sizes[size].root}>
      {(image?.src ?? size !== 'lg') && (
        <Link href={href} className={sizes[size].image} onClick={() => setOpenBook(false)}>
          <IconPhoto className="text-muted size-12" />

          {image?.src && (
            <>
              {event.preserveAspectRatio && (
                <div className="absolute inset-0 z-10 overflow-hidden rounded-[calc(var(--radius)-1px)] bg-black/25 backdrop-blur-lg">
                  <Image {...image} alt={event.name} fill sizes={sizes[size].imageSize} className="object-contain" />
                </div>
              )}

              <Image {...image} alt={event.name} fill sizes={sizes[size].imageSize} className="object-cover" />
            </>
          )}
        </Link>
      )}

      <div className={sizes[size].content}>
        <Link href={href} className="group flex flex-col" onClick={() => setOpenBook(false)}>
          <Text element="p" className="text-muted-foreground mb-0! text-sm font-medium">
            {size === 'lg' && !dateFormat
              ? formatTimeRange(event.dates, event.dates_end) || '6:00 PM'
              : `${format(date, dateFormat || 'EE do')} at ${formatTimeRange(event.dates, event.dates_end) || '6:00 PM'}`}
          </Text>
          <Text element={'h4'} className="text-base underline-offset-4 group-hover:underline sm:text-lg">
            {event.eventName}
          </Text>
        </Link>

        {place?.custom?.displayName && (
          <Text element="p" className="text-muted-foreground flex items-center gap-2 text-sm">
            <IconMapPinFilled className="size-4" />
            {place.custom.displayName}
          </Text>
        )}

        <div className="flex w-full">
          {event.shortDescription && (
            <RichText
              className={cn('text-muted-foreground! line-clamp-2 w-0 grow text-sm', size !== 'lg' && 'sm:line-clamp-3')}
            >
              {event.shortDescription}
            </RichText>
          )}
        </div>

        <div className="mt-2 flex items-center gap-3">
          <Button
            onClick={onClick}
            disabled={['Sold Out', 'Cancelled'].includes(buttonText)}
            variant="outline"
            size="sm"
          >
            {buttonText}
          </Button>

          <EventActions event={event} />
        </div>
      </div>
    </div>
  );
}
